/**
 * ChallengeOverlayBridge — Bridges between GameStateCoordinator and the EduAdventure motor.
 *
 * Responsibilities:
 * 1. Creates/manages the overlay Canvas DOM element (z-index 1000, above Phaser)
 * 2. Implements the OverlayCallbacks interface from GameStateCoordinator
 * 3. Handles the "Pronto?" prompt UI
 * 4. Initializes the EduAdventure motor with challenge data (game.json subset)
 * 5. Reuses existing bundle.js (370KB) without duplication
 * 6. Uses existing EduAdventure fit mode with letterboxing (motor handles internally)
 * 7. Wires completion/exit callbacks to GameStateCoordinator
 *
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5, 4.6, 4.7, 8.4, 10.5
 */

import type { OverlayCallbacks } from './GameStateCoordinator';
import { IframeMotorFactory } from './EduAdventureMotorAdapter';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Interface for the EduAdventure motor instance.
 * The motor is loaded via bundle.js and exposes these methods globally.
 */
export interface EduAdventureMotor {
  /** Initialize the motor with game.json data on a given canvas */
  init(canvas: HTMLCanvasElement, gameData: object): void;
  /** Stop the motor and clean up */
  stop(): void;
  /** Register a callback for when all challenges are completed */
  onComplete(callback: () => void): void;
  /** Register a callback for when the player exits early */
  onExit(callback: () => void): void;
}

/**
 * Configuration for the ChallengeOverlayBridge.
 */
export interface ChallengeOverlayBridgeConfig {
  /** The parent container element where the overlay will be appended */
  container: HTMLElement;
  /** Optional: custom z-index for the overlay (default: 1000) */
  zIndex?: number;
  /** Optional: callback when all challenges in a sequence are completed */
  onChallengeComplete?: (caveId: string) => void;
  /** Optional: callback when the player exits a challenge early */
  onChallengeExit?: () => void;
  /** Optional: custom motor factory for testing */
  motorFactory?: MotorFactory;
}

/**
 * Factory interface for creating motor instances.
 * Allows injection of mock motors for testing.
 */
export interface MotorFactory {
  create(canvas: HTMLCanvasElement, gameData: object): EduAdventureMotor;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_Z_INDEX = 1000;
const OVERLAY_ID = 'challenge-overlay-container';
const CANVAS_ID = 'challenge-overlay-canvas';
const PROMPT_ID = 'challenge-overlay-prompt';

// ─── Default Motor Factory ────────────────────────────────────────────────────

/**
 * Default motor factory that interfaces with the globally-loaded EduAdventure bundle.js.
 *
 * The bundle.js IIFE exposes the motor on `window.EduAdventureMotor` when loaded.
 * This factory creates a motor instance that:
 * - Uses the overlay canvas (not the main #game canvas)
 * - Receives game.json data directly (no fetch needed)
 * - Wires completion/exit callbacks
 */
class DefaultMotorFactory implements MotorFactory {
  create(canvas: HTMLCanvasElement, gameData: object): EduAdventureMotor {
    // The EduAdventure motor from bundle.js operates on a canvas element.
    // We create a lightweight adapter that interfaces with the global motor API.
    return new BundleMotorAdapter(canvas, gameData);
  }
}

/**
 * Adapter that wraps the EduAdventure bundle.js motor for overlay use.
 *
 * The existing motor auto-boots on a #game canvas. For overlay use, we:
 * 1. Set the overlay canvas as the target
 * 2. Inject game.json data directly (bypassing fetch)
 * 3. Wire completion/exit events
 */
class BundleMotorAdapter implements EduAdventureMotor {
  private _canvas: HTMLCanvasElement;
  private _gameData: object;
  private _completeCallback: (() => void) | null = null;
  private _exitCallback: (() => void) | null = null;
  private _running = false;
  private _animFrameId: number | null = null;

  constructor(canvas: HTMLCanvasElement, gameData: object) {
    this._canvas = canvas;
    this._gameData = gameData;
  }

  init(canvas: HTMLCanvasElement, gameData: object): void {
    this._canvas = canvas;
    this._gameData = gameData;
    this._running = true;

    // Access the global EduAdventure motor API from bundle.js
    const globalMotor = (window as any).EduAdventureMotor;
    if (globalMotor && typeof globalMotor.initOverlay === 'function') {
      globalMotor.initOverlay(canvas, gameData, {
        onComplete: () => this._handleComplete(),
        onExit: () => this._handleExit(),
      });
    } else {
      // Fallback: if the global motor API isn't available yet,
      // dispatch a custom event that bundle.js can listen for
      const event = new CustomEvent('eduadventure-overlay-init', {
        detail: {
          canvas,
          gameData,
          onComplete: () => this._handleComplete(),
          onExit: () => this._handleExit(),
        },
      });
      window.dispatchEvent(event);
    }
  }

  stop(): void {
    this._running = false;

    const globalMotor = (window as any).EduAdventureMotor;
    if (globalMotor && typeof globalMotor.stopOverlay === 'function') {
      globalMotor.stopOverlay();
    } else {
      // Dispatch stop event as fallback
      window.dispatchEvent(new CustomEvent('eduadventure-overlay-stop'));
    }

    if (this._animFrameId !== null) {
      cancelAnimationFrame(this._animFrameId);
      this._animFrameId = null;
    }

    // Clear the canvas
    const ctx = this._canvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, this._canvas.width, this._canvas.height);
    }
  }

  onComplete(callback: () => void): void {
    this._completeCallback = callback;
  }

  onExit(callback: () => void): void {
    this._exitCallback = callback;
  }

  private _handleComplete(): void {
    if (this._completeCallback) {
      this._completeCallback();
    }
  }

  private _handleExit(): void {
    if (this._exitCallback) {
      this._exitCallback();
    }
  }
}

// ─── ChallengeOverlayBridge Class ─────────────────────────────────────────────

export class ChallengeOverlayBridge implements OverlayCallbacks {
  private _container: HTMLElement;
  private _zIndex: number;
  private _overlayElement: HTMLDivElement | null = null;
  private _canvasElement: HTMLCanvasElement | null = null;
  private _promptElement: HTMLDivElement | null = null;
  private _motorFactory: MotorFactory;
  private _activeMotor: EduAdventureMotor | null = null;
  private _onChallengeComplete: ((caveId: string) => void) | null;
  private _onChallengeExit: (() => void) | null;
  private _activeCaveId: string | null = null;
  private _isVisible = false;

  constructor(config: ChallengeOverlayBridgeConfig) {
    this._container = config.container;
    this._zIndex = config.zIndex ?? DEFAULT_Z_INDEX;
    this._onChallengeComplete = config.onChallengeComplete ?? null;
    this._onChallengeExit = config.onChallengeExit ?? null;
    this._motorFactory = config.motorFactory ?? new IframeMotorFactory();

    this._createDOMElements();
  }

  // ─── OverlayCallbacks Implementation ────────────────────────────────────────

  /**
   * Show the overlay container (makes it visible, full viewport coverage).
   * The overlay covers the Phaser canvas with z-index 1000.
   */
  show(): void {
    if (!this._overlayElement) {
      this._createDOMElements();
    }
    this._overlayElement!.style.display = 'flex';
    this._isVisible = true;
  }

  /**
   * Hide the overlay container (display: none).
   */
  hide(): void {
    if (this._overlayElement) {
      this._overlayElement.style.display = 'none';
    }
    this._hidePrompt();
    this._isVisible = false;
  }

  /**
   * Show the "Pronto?" prompt and wait for player confirmation.
   * Returns true if confirmed, false if declined/exited.
   */
  showPrompt(message: string): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if (!this._promptElement || !this._overlayElement) {
        resolve(true); // If no DOM, auto-confirm
        return;
      }

      // Hide canvas while showing prompt
      if (this._canvasElement) {
        this._canvasElement.style.display = 'none';
      }

      // Build prompt UI
      this._promptElement.innerHTML = '';
      this._promptElement.style.display = 'flex';

      const promptContent = document.createElement('div');
      promptContent.className = 'challenge-prompt-content';
      promptContent.style.cssText = `
        text-align: center;
        padding: 32px;
        border-radius: 20px;
        background: rgba(20, 33, 61, 0.95);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
        max-width: 400px;
        width: 90%;
      `;

      const messageEl = document.createElement('h2');
      messageEl.textContent = message;
      messageEl.style.cssText = `
        color: #ffffff;
        font-family: system-ui, -apple-system, sans-serif;
        font-size: 36px;
        margin: 0 0 24px 0;
      `;

      const confirmBtn = document.createElement('button');
      confirmBtn.textContent = 'Sim!';
      confirmBtn.style.cssText = `
        font-size: 24px;
        padding: 16px 40px;
        border-radius: 14px;
        border: none;
        background: #38B000;
        color: #ffffff;
        cursor: pointer;
        margin: 0 8px;
        font-weight: bold;
        min-width: 120px;
        touch-action: manipulation;
      `;

      const exitBtn = document.createElement('button');
      exitBtn.textContent = 'Voltar';
      exitBtn.style.cssText = `
        font-size: 20px;
        padding: 12px 28px;
        border-radius: 14px;
        border: 2px solid rgba(255, 255, 255, 0.3);
        background: transparent;
        color: rgba(255, 255, 255, 0.8);
        cursor: pointer;
        margin: 12px 8px 0;
        touch-action: manipulation;
      `;

      promptContent.appendChild(messageEl);
      promptContent.appendChild(confirmBtn);
      promptContent.appendChild(document.createElement('br'));
      promptContent.appendChild(exitBtn);
      this._promptElement.appendChild(promptContent);

      const cleanup = () => {
        confirmBtn.removeEventListener('click', onConfirm);
        confirmBtn.removeEventListener('touchend', onConfirm);
        exitBtn.removeEventListener('click', onExit);
        exitBtn.removeEventListener('touchend', onExit);
      };

      const onConfirm = (e: Event) => {
        e.preventDefault();
        cleanup();
        this._hidePrompt();
        if (this._canvasElement) {
          this._canvasElement.style.display = 'block';
        }
        resolve(true);
      };

      const onExit = (e: Event) => {
        e.preventDefault();
        cleanup();
        this._hidePrompt();
        resolve(false);
      };

      confirmBtn.addEventListener('click', onConfirm);
      confirmBtn.addEventListener('touchend', onConfirm);
      exitBtn.addEventListener('click', onExit);
      exitBtn.addEventListener('touchend', onExit);
    });
  }

  /**
   * Initialize the EduAdventure motor with challenge data.
   * The motor handles its own viewport (fit mode with letterboxing).
   */
  initializeMotor(gameJson: object): void {
    if (!this._canvasElement) {
      return;
    }

    // Resize canvas to fill viewport
    this._resizeCanvas();

    // Create motor instance via factory
    this._activeMotor = this._motorFactory.create(this._canvasElement, gameJson);

    // Wire completion callback
    this._activeMotor.onComplete(() => {
      if (this._onChallengeComplete && this._activeCaveId) {
        this._onChallengeComplete(this._activeCaveId);
      }
    });

    // Wire exit callback
    this._activeMotor.onExit(() => {
      if (this._onChallengeExit) {
        this._onChallengeExit();
      }
    });

    // Initialize the motor on the canvas
    this._activeMotor.init(this._canvasElement, gameJson);
  }

  /**
   * Stop the motor and clean up.
   */
  stopMotor(): void {
    if (this._activeMotor) {
      this._activeMotor.stop();
      this._activeMotor = null;
    }
  }

  // ─── Public API (beyond OverlayCallbacks) ───────────────────────────────────

  /**
   * Set the active cave ID for completion callback routing.
   */
  setActiveCaveId(caveId: string | null): void {
    this._activeCaveId = caveId;
  }

  /**
   * Get the current visibility state.
   */
  get isVisible(): boolean {
    return this._isVisible;
  }

  /**
   * Get the overlay canvas element (for testing or external access).
   */
  get canvas(): HTMLCanvasElement | null {
    return this._canvasElement;
  }

  /**
   * Get the overlay container element.
   */
  get overlayElement(): HTMLDivElement | null {
    return this._overlayElement;
  }

  /**
   * Destroy the bridge and remove DOM elements.
   */
  destroy(): void {
    this.stopMotor();
    if (this._overlayElement && this._overlayElement.parentNode) {
      this._overlayElement.parentNode.removeChild(this._overlayElement);
    }
    this._overlayElement = null;
    this._canvasElement = null;
    this._promptElement = null;
    this._isVisible = false;
  }

  // ─── Private Methods ────────────────────────────────────────────────────────

  /**
   * Create the DOM elements for the overlay:
   * - Container div (positioned absolute, z-index 1000, full viewport)
   * - Canvas element (for the EduAdventure motor)
   * - Prompt div (for "Pronto?" UI)
   */
  private _createDOMElements(): void {
    // Container: positioned absolutely above Phaser canvas
    this._overlayElement = document.createElement('div');
    this._overlayElement.id = OVERLAY_ID;
    this._overlayElement.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: ${this._zIndex};
      display: none;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, 0.85);
      overflow: hidden;
    `;

    // Canvas: for the EduAdventure motor rendering
    this._canvasElement = document.createElement('canvas');
    this._canvasElement.id = CANVAS_ID;
    this._canvasElement.style.cssText = `
      display: block;
      width: 100%;
      height: 100%;
      touch-action: none;
    `;

    // Prompt: for "Pronto?" UI overlay
    this._promptElement = document.createElement('div');
    this._promptElement.id = PROMPT_ID;
    this._promptElement.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: none;
      align-items: center;
      justify-content: center;
      z-index: 1;
    `;

    // Assemble DOM
    this._overlayElement.appendChild(this._canvasElement);
    this._overlayElement.appendChild(this._promptElement);
    this._container.appendChild(this._overlayElement);
  }

  /**
   * Hide the prompt element.
   */
  private _hidePrompt(): void {
    if (this._promptElement) {
      this._promptElement.style.display = 'none';
      this._promptElement.innerHTML = '';
    }
  }

  /**
   * Resize the canvas to match the current viewport.
   * The EduAdventure motor handles its own internal scaling (fit mode with letterboxing).
   */
  private _resizeCanvas(): void {
    if (!this._canvasElement) return;

    // Use visualViewport for accurate mobile sizing
    const vv = window.visualViewport;
    const width = vv ? Math.round(vv.width) : window.innerWidth;
    const height = vv ? Math.round(vv.height) : window.innerHeight;

    this._canvasElement.width = width * (window.devicePixelRatio || 1);
    this._canvasElement.height = height * (window.devicePixelRatio || 1);
  }
}
